import React, { useState, useEffect } from 'react';

const ImageModal = ({ isOpen, onClose, imageUrl, images = [] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Normalize image source
  let imgList = [];
  if (Array.isArray(images) && images.length > 0) {
    imgList = images;
  } else if (Array.isArray(imageUrl) && imageUrl.length > 0) {
    imgList = imageUrl;
  } else if (typeof imageUrl === 'string' && imageUrl) {
    imgList = [imageUrl];
  } else if (typeof images === 'string' && images) {
    imgList = [images];
  }

  // Reset index when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(0);
    }
  }, [isOpen, imgList.length]);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? imgList.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === imgList.length - 1 ? 0 : prev + 1));
  };

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen || imgList.length <= 1) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, imgList.length]);

  // Close modal on Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen || imgList.length === 0) return null;

  const showNavigation = imgList.length > 1;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-[100] p-4 select-none" onClick={onClose}>
      <div className="relative max-w-4xl w-full max-h-[90vh] flex flex-col items-center justify-center" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button 
          onClick={onClose} 
          className="absolute -top-12 right-0 text-white hover:text-[#FDA729] focus:outline-none transition-colors"
        >
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Slideshow Content */}
        <div className="relative w-full flex items-center justify-center">
          {showNavigation && (
            <button 
              onClick={handlePrev} 
              className="absolute left-2 md:-left-16 bg-black/40 hover:bg-black/60 text-white hover:text-[#FDA729] rounded-full p-2.5 transition-all z-10"
              title="Previous"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          {/* Active Image */}
          <div className="w-full flex justify-center items-center overflow-hidden rounded-lg shadow-2xl bg-white border border-gray-200">
            <img 
              src={imgList[currentIndex]} 
              alt={`Slide ${currentIndex + 1}`} 
              className="max-w-full max-h-[80vh] object-contain transition-opacity duration-300 ease-in-out"
            />
          </div>

          {showNavigation && (
            <button 
              onClick={handleNext} 
              className="absolute right-2 md:-right-16 bg-black/40 hover:bg-black/60 text-white hover:text-[#FDA729] rounded-full p-2.5 transition-all z-10"
              title="Next"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>

        {/* Indicators */}
        {showNavigation && (
          <div className="mt-4 flex flex-col items-center space-y-2">
            {/* Dots */}
            <div className="flex space-x-2">
              {imgList.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`h-2.5 rounded-full transition-all duration-300 ${
                    index === currentIndex ? 'w-6 bg-[#FDA729]' : 'w-2.5 bg-white/50 hover:bg-white'
                  }`}
                />
              ))}
            </div>
            {/* Slide Index Text */}
            <span className="text-white/70 text-xs font-medium">
              {currentIndex + 1} / {imgList.length}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageModal;
